import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Card,
  CardBody,
  Button,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import {
  CalendarBlank,
  MapPin,
  CurrencyDollar,
  FileText,
  DownloadSimple,
  ArrowLeft,
  Columns,
} from 'phosphor-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import moment from 'moment';
import 'moment/locale/pt-br';
import SecaoClienteLayout from '../layout';
import { api } from '../../../services/api';
import { formatCurrency } from '../../../utils/FormatFunctions';

interface ContratoCliente {
  id: number;
  fornecedor_nome: string;
  valor: number;
  status: string;
  categoria_nome?: string;
  categoria_cor?: string;
  nf_numero?: string;
  dt_vencimento?: string;
}

interface DocumentoCliente {
  id: number;
  tipo: string;
  nome: string;
  dt_criacao?: string;
}

interface EventoClienteData {
  id: number;
  nome: string;
  artista?: string;
  dt_evento?: string;
  local?: string;
  cidade?: string;
  uf?: string;
  valor_orcamento: number;
  status: string;
  kanban_projeto_id?: number;
  contratos: ContratoCliente[];
  documentos: DocumentoCliente[];
}

const STATUS_COLORS: Record<string, string> = {
  proposta: 'yellow',
  contrato: 'blue',
  assinado: 'cyan',
  nf_emitida: 'purple',
  pago: 'green',
};

const STATUS_LABELS: Record<string, string> = {
  proposta: 'Proposta',
  contrato: 'Contrato',
  assinado: 'Assinado',
  nf_emitida: 'NF Emitida',
  pago: 'Pago',
};

export default function EventoClienteDetalhe() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const navigate = useNavigate();
  const [evento, setEvento] = useState<EventoClienteData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clienteData = JSON.parse(localStorage.getItem('cliente_data') || '{}');

  React.useEffect(() => {
    const fetchEvento = async () => {
      try {
        const token = localStorage.getItem('cliente_access_token');
        const response = await api.get(`/api/v1/secao-cliente/eventos/${eventoId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEvento(response.data?.data ?? null);
      } catch {
        setEvento(null);
      } finally {
        setIsLoading(false);
      }
    };
    if (eventoId && clienteData.id) fetchEvento();
    else setIsLoading(false);
  }, [eventoId, clienteData.id]);

  // formatCurrency importado de FormatFunctions

  const handleDownload = async (docId: number) => {
    try {
      const token = localStorage.getItem('cliente_access_token');
      const response = await api.get(
        `/api/v1/secao-cliente/eventos/${eventoId}/documentos/${docId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const url = response.data?.data?.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('Erro ao baixar documento');
    }
  };

  // Agrupar contratos por categoria
  const groupedContratos: Record<string, ContratoCliente[]> = {};
  (evento?.contratos ?? []).forEach(c => {
    const key = c.categoria_nome || 'Outros';
    if (!groupedContratos[key]) groupedContratos[key] = [];
    groupedContratos[key].push(c);
  });

  return (
    <SecaoClienteLayout>
      <Box p={6}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft size={16} />}
          mb={4}
          onClick={() => navigate('/secao-cliente/eventos')}
        >
          Voltar
        </Button>

        {isLoading && (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color="blue.400" />
          </Flex>
        )}

        {!isLoading && !evento && (
          <Flex justify="center" py={10}>
            <Text color="gray.400">Evento não encontrado</Text>
          </Flex>
        )}

        {evento && (
          <>
            {/* Header */}
            <Card mb={6}>
              <CardBody>
                <Heading size="md" color="gray.800">
                  {evento.nome}
                </Heading>
                <HStack spacing={4} mt={2} fontSize="xs" color="gray.500">
                  {evento.dt_evento && (
                    <HStack spacing={1}>
                      <CalendarBlank size={12} />
                      <Text>{moment(evento.dt_evento).format('DD/MM/YYYY')}</Text>
                    </HStack>
                  )}
                  {evento.local && (
                    <HStack spacing={1}>
                      <MapPin size={12} />
                      <Text>{evento.local}</Text>
                    </HStack>
                  )}
                  <HStack spacing={1}>
                    <CurrencyDollar size={12} />
                    <Text fontWeight="600">{formatCurrency(evento.valor_orcamento)}</Text>
                  </HStack>
                </HStack>
                {evento.kanban_projeto_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="12px"
                    leftIcon={<Columns size={14} />}
                    mt={3}
                    onClick={() => navigate(`/secao-cliente/eventos/${eventoId}/kanban`)}
                    _hover={{ transform: 'translateY(-1px)' }}
                    transition="all 0.15s ease"
                  >
                    Ver Kanban
                  </Button>
                )}
              </CardBody>
            </Card>

            {/* Tabs */}
            <Tabs variant="enclosed" colorScheme="blue" size="sm">
              <TabList>
                <Tab>Contratos ({evento.contratos.length})</Tab>
                <Tab>Documentos ({evento.documentos.length})</Tab>
              </TabList>
              <TabPanels>
                {/* Contratos */}
                <TabPanel px={0}>
                  {Object.entries(groupedContratos).map(([catNome, items]) => (
                    <Box key={catNome} mb={4}>
                      <HStack spacing={2} mb={2}>
                        {items[0]?.categoria_cor && (
                          <Box w="3px" h="16px" borderRadius="full" bg={items[0].categoria_cor} />
                        )}
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color={items[0]?.categoria_cor || 'gray.600'}
                        >
                          {catNome}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {formatCurrency(items.reduce((s, c) => s + c.valor, 0))}
                        </Text>
                      </HStack>
                      <VStack spacing={2} align="stretch">
                        {items.map(c => (
                          <Flex
                            key={c.id}
                            bg="white"
                            p={3}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.100"
                            justify="space-between"
                            align="center"
                          >
                            <HStack spacing={3}>
                              <Icon as={FileText} color="gray.400" />
                              <Box>
                                <Text fontSize="sm" fontWeight="600">
                                  {c.fornecedor_nome}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {formatCurrency(c.valor)}
                                </Text>
                              </Box>
                            </HStack>
                            <Badge colorScheme={STATUS_COLORS[c.status] || 'gray'} fontSize="xs">
                              {STATUS_LABELS[c.status] || c.status}
                            </Badge>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                  {evento.contratos.length === 0 && (
                    <Text color="gray.400" textAlign="center" py={6}>
                      Nenhum contrato
                    </Text>
                  )}
                </TabPanel>

                {/* Documentos */}
                <TabPanel px={0}>
                  <VStack spacing={2} align="stretch">
                    {evento.documentos.map(doc => (
                      <Flex
                        key={doc.id}
                        bg="white"
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.100"
                        justify="space-between"
                        align="center"
                      >
                        <HStack spacing={3}>
                          <Icon as={FileText} color="blue.400" />
                          <Box>
                            <Text fontSize="sm" fontWeight="600">
                              {doc.nome}
                            </Text>
                            {doc.dt_criacao && (
                              <Text fontSize="2xs" color="gray.400">
                                {moment(doc.dt_criacao).format('DD/MM/YYYY')}
                              </Text>
                            )}
                          </Box>
                        </HStack>
                        <Button
                          size="xs"
                          variant="ghost"
                          leftIcon={<DownloadSimple size={14} />}
                          onClick={() => handleDownload(doc.id)}
                        >
                          Baixar
                        </Button>
                      </Flex>
                    ))}
                  </VStack>
                  {evento.documentos.length === 0 && (
                    <Text color="gray.400" textAlign="center" py={6}>
                      Nenhum documento
                    </Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>
    </SecaoClienteLayout>
  );
}
